import React, { useCallback, useEffect, useState } from 'react';
import { FiChevronRight } from 'react-icons/fi';
import { getColumns, Column } from '../services/ColumnService';
import { getUsersSummaryForPicker, User } from '../services/UserService';
import { useSector } from '../contexts/SectorContext';
import { ConfirmModal } from '../components/SharedModal';
import Switch from '../components/Switch/Switch';
import {
  GoogleAdsLeadEvent,
  GoogleAdsLeadIntegration,
  GoogleAdsLeadSeenColumn,
  createGoogleAdsLeadIntegration,
  deleteGoogleAdsLeadIntegration,
  getGoogleAdsLeadIntegration,
  listGoogleAdsLeadColumns,
  listGoogleAdsLeadIntegrations,
  reprocessGoogleAdsLeadEvent,
  rotateGoogleAdsLeadSecret,
  updateGoogleAdsLeadIntegration,
} from '../services/GoogleAdsLeadService';
import {
  Draft,
  LeadSourceType,
  SOURCE_TYPE_OPTIONS,
  draftFromIntegration,
  emptyDraft,
  seenColumnOptions,
  validateDraft,
  writeFromDraft,
} from './googleAdsLeadDraft';
import { phoneCountryOptions } from '../utils/phoneCountries';
import ProspectRuleSection from '../components/ProspectRuleSection';
import { listLeadStatuses, type ProspectLeadStatus } from '../services/ProspectingService';
import './GoogleAdsLeadsPage.css';

const UNDEFINED_SOURCE_LABEL = 'Não definido (Google Ads só se o lead vier com dados do Google)';

function formatHour(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/** Coluna "Contato" da lista de leads: o que o contato automático fez com cada lead. */
export function contactLine(status?: ProspectLeadStatus): { text: string; tone: 'ok' | 'wait' | 'bad'; href?: string } | null {
  if (!status) return null;
  switch (status.status) {
    case 'active':
      return { text: `Mensagem enviada ${formatHour(status.firstSentAtUtc)}`.trim(), tone: 'ok' };
    case 'replied':
      return { text: 'Respondeu', tone: 'ok' };
    case 'awaiting_approval':
      return {
        text: 'Aguardando sua aprovação',
        tone: 'wait',
        href: status.approvalId ? `/central-inteligencia?tab=aprovacoes&id=${status.approvalId}` : undefined,
      };
    case 'pending':
    case 'sending':
      return { text: 'Na fila', tone: 'wait' };
    case 'skipped':
      return { text: `Pulado: ${status.reasonLabel || 'motivo não identificado'}`, tone: 'wait' };
    case 'failed':
      return { text: `Falhou: ${status.reasonLabel || 'motivo não identificado'}`, tone: 'bad' };
    default:
      return null;
  }
}

function sourceTypeLabel(type?: string | null) {
  return SOURCE_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? 'Não definido';
}

function errorLabel(code?: string | null) {
  switch (code) {
    case 'missing_phone': return 'Sem telefone';
    case 'missing_column': return 'Sem etapa no CRM';
    case 'ambiguous_contact': return 'Telefone em mais de um contato';
    case 'contact_missing': return 'Contato não está neste setor';
    case 'integration_missing': return 'Integração não encontrada';
    case 'processing_error': return 'Falha ao processar';
    default: return code || '';
  }
}

function statusLabel(status: string) {
  switch (status) {
    case 'received': return 'Na fila';
    case 'processing': return 'Processando';
    case 'processed': return 'No CRM';
    case 'needs_review': return 'Revisar';
    case 'failed': return 'Falhou';
    default: return status;
  }
}

function formatWhen(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function activityLine(item: GoogleAdsLeadIntegration) {
  const lead = formatWhen(item.lastProductionReceivedAt);
  if (lead) return `Último lead ${lead}`;
  const test = formatWhen(item.lastTestReceivedAt);
  if (test) return `Teste ${test}`;
  return 'Nenhum lead ainda';
}

async function copyText(value: string) {
  await navigator.clipboard.writeText(value);
}

const GoogleAdsLeadsPage: React.FC = () => {
  const { selectedSectorId, isOfficial } = useSector();
  const [items, setItems] = useState<GoogleAdsLeadIntegration[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [events, setEvents] = useState<GoogleAdsLeadEvent[]>([]);
  const [seenColumns, setSeenColumns] = useState<GoogleAdsLeadSeenColumn[]>([]);
  const [statuses, setStatuses] = useState<Record<string, ProspectLeadStatus>>({});
  const [columns, setColumns] = useState<Column[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const selected = items.find((item) => item.id === selectedId) ?? null;
  const showCreate = !loading && (creating || items.length === 0);

  const loadList = useCallback(async () => {
    if (!selectedSectorId) return [] as GoogleAdsLeadIntegration[];
    const rows = await listGoogleAdsLeadIntegrations(selectedSectorId);
    setItems(rows);
    setSelectedId((current) => (current && rows.some((row) => row.id === current) ? current : rows[0]?.id ?? null));
    return rows;
  }, [selectedSectorId]);

  const applyIntegration = useCallback((integration: GoogleAdsLeadIntegration, nextEvents?: GoogleAdsLeadEvent[]) => {
    if (nextEvents) setEvents(nextEvents);
    setItems((current) => current.map((item) => (item.id === integration.id ? integration : item)));
    setDraft(draftFromIntegration(integration));
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    const detail = await getGoogleAdsLeadIntegration(id);
    applyIntegration(detail.integration, detail.events ?? []);
    listGoogleAdsLeadColumns(id).then(setSeenColumns).catch(() => setSeenColumns([]));
    if (selectedSectorId) {
      listLeadStatuses(selectedSectorId, id)
        .then(rows => setStatuses(Object.fromEntries((rows ?? []).map(r => [r.eventId, r]))))
        .catch(() => setStatuses({}));
    }
  }, [applyIntegration, selectedSectorId]);

  useEffect(() => {
    if (!selectedSectorId) return;
    setError('');
    setNotice('');
    setSecret(null);
    setCreating(false);
    setSelectedId(null);
    setEvents([]);
    setSeenColumns([]);
    setDraft(emptyDraft());
    setLoading(true);
    Promise.all([
      loadList(),
      getColumns().catch(() => [] as Column[]),
      getUsersSummaryForPicker(selectedSectorId).catch(() => [] as User[]),
    ])
      .then(([rows, cols, people]) => {
        setColumns(cols.filter((column) => column.sectorId === selectedSectorId));
        setUsers(people);
        const first = rows[0];
        if (first) setDraft(draftFromIntegration(first));
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedSectorId, loadList]);

  useEffect(() => {
    if (!selectedId || creating) {
      if (!selectedId) setEvents([]);
      return;
    }
    loadDetail(selectedId).catch((err: Error) => setError(err.message));
  }, [selectedId, creating, loadDetail]);

  const bodyFrom = (source: Draft) => writeFromDraft(selectedSectorId!, source);

  const startCreate = () => {
    setCreating(true);
    setSecret(null);
    setNotice('');
    setError('');
    setDraft(emptyDraft());
  };

  const cancelCreate = () => {
    setCreating(false);
    setError('');
    if (selectedId) {
      loadDetail(selectedId).catch((err: Error) => setError(err.message));
    }
  };

  const selectItem = (id: string) => {
    setCreating(false);
    setSecret(null);
    setNotice('');
    setError('');
    setSelectedId(id);
  };

  const onToggle = async (next: boolean) => {
    const previous = draft.enabled;
    setDraft((current) => ({ ...current, enabled: next }));
    if (showCreate || !selected) return;
    setToggling(true);
    setError('');
    try {
      const updated = await updateGoogleAdsLeadIntegration(
        selected.id,
        writeFromDraft(selected.sectorId, { ...draftFromIntegration(selected), enabled: next }),
      );
      setItems((current) => current.map((item) => (item.id === selected.id ? { ...item, ...updated } : item)));
      setNotice(next ? 'Recebendo leads.' : 'Recebimento pausado.');
    } catch (err) {
      setDraft((current) => ({ ...current, enabled: previous }));
      setError(err instanceof Error ? err.message : 'Não foi possível atualizar.');
    } finally {
      setToggling(false);
    }
  };

  const onCreate = async () => {
    if (!selectedSectorId) return;
    const invalid = validateDraft(draft);
    if (invalid) {
      setError(invalid);
      return;
    }
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const created = await createGoogleAdsLeadIntegration(bodyFrom(draft));
      setSecret(created.secret);
      setCreating(false);
      setSelectedId(created.integration.id);
      setNotice('Integração criada. Copie a chave agora.');
      await loadList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao criar.');
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    if (!selected) return;
    const invalid = validateDraft(draft);
    if (invalid) {
      setError(invalid);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateGoogleAdsLeadIntegration(selected.id, bodyFrom(draft));
      setNotice('Salvo.');
      await loadList();
      await loadDetail(selected.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const onRotate = async () => {
    if (!selected) return;
    setRotating(true);
    setError('');
    try {
      const rotated = await rotateGoogleAdsLeadSecret(selected.id);
      setSecret(rotated.secret);
      setNotice('Chave nova. Cole no formulário, no site ou na ferramenta que envia os leads.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao gerar a chave.');
    } finally {
      setRotating(false);
    }
  };

  const onDelete = async () => {
    if (!selected) return;
    const deletedId = selected.id;
    setDeleting(true);
    setError('');
    try {
      await deleteGoogleAdsLeadIntegration(deletedId);
      setSecret(null);
      setEvents([]);
      setNotice('Integração excluída.');
      setCreating(false);
      const rows = await listGoogleAdsLeadIntegrations(selectedSectorId!);
      setItems(rows);
      const next = rows.find((row) => row.id !== deletedId) ?? null;
      setSelectedId(next?.id ?? null);
      if (!next) setDraft(emptyDraft());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível excluir.');
    } finally {
      setDeleting(false);
    }
  };

  const onReprocess = async (eventId: string) => {
    if (!selected) return;
    setError('');
    try {
      await reprocessGoogleAdsLeadEvent(selected.id, eventId);
      setNotice('Lead de volta na fila.');
      await loadDetail(selected.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao tentar de novo.');
    }
  };

  if (!selectedSectorId) {
    return (
      <div className="google-ads-leads">
        <header className="google-ads-header">
          <div>
            <h1>Formulários (Google Ads e sites)</h1>
            <p>Selecione uma conexão no menu.</p>
          </div>
        </header>
      </div>
    );
  }

  return (
    <div className="google-ads-leads">
      <header className="google-ads-header">
        <div className="google-ads-header-copy">
          <h1>Formulários (Google Ads e sites)</h1>
          <p>Cole o URL e a chave no formulário do anúncio, no site ou na ferramenta de integração (Zapier, Make, RD Station).</p>
        </div>
        {items.length > 0 ? (
          <button
            type="button"
            className="google-ads-btn"
            onClick={startCreate}
            disabled={loading || showCreate}
          >
            Nova integração
          </button>
        ) : null}
      </header>

      <div className="google-ads-body">
        {error ? <p className="google-ads-banner is-error" role="alert">{error}</p> : null}
        {notice ? <p className="google-ads-banner" role="status">{notice}</p> : null}
        {loading ? <p className="google-ads-hint">Carregando…</p> : null}

        {!loading ? (
          <div className={`google-ads-layout${items.length === 0 ? ' is-single' : ''}`}>
            {items.length > 0 ? (
              <section className="google-ads-panel" aria-label="Integrações">
                <h2>Integrações</h2>
                <div className="google-ads-list">
                  {items.map((item) => {
                    const active = item.id === selectedId && !showCreate;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`google-ads-item${active ? ' is-active' : ''}`}
                        aria-current={active ? 'true' : undefined}
                        onClick={() => selectItem(item.id)}
                      >
                        <span className="google-ads-item-name">{item.name}</span>
                        <span className={`google-ads-status${item.enabled ? ' is-on' : ''}`}>
                          {item.enabled ? 'Ativa' : 'Pausada'}
                        </span>
                        <span className="google-ads-item-meta">{sourceTypeLabel(item.sourceType)} · {activityLine(item)}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}

            <section className="google-ads-panel" aria-label={showCreate ? 'Nova integração' : selected?.name || 'Integração'}>
              <h2>{showCreate ? 'Nova integração' : selected?.name || 'Integração'}</h2>
              {showCreate ? <p className="google-ads-hint">Depois de criar, copie o URL e a chave.</p> : null}

              <div className="google-ads-field">
                <label htmlFor="gads-name">Nome</label>
                <input
                  id="gads-name"
                  value={draft.name}
                  placeholder="Ex.: Formulário de orçamento"
                  onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                />
              </div>

              <div className="google-ads-field">
                <label htmlFor="gads-source">Tipo de fonte</label>
                <select
                  id="gads-source"
                  value={draft.sourceType}
                  onChange={(event) => setDraft((current) => ({ ...current, sourceType: event.target.value as LeadSourceType | '' }))}
                >
                  {draft.sourceType === '' ? <option value="">{UNDEFINED_SOURCE_LABEL}</option> : null}
                  {SOURCE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {draft.sourceType === 'other' ? (
                <div className="google-ads-field">
                  <label htmlFor="gads-source-label">Nome da integração</label>
                  <input
                    id="gads-source-label"
                    maxLength={60}
                    value={draft.sourceLabel}
                    placeholder="Ex.: RD Station"
                    onChange={(event) => setDraft((current) => ({ ...current, sourceLabel: event.target.value }))}
                  />
                </div>
              ) : null}

              <Switch
                checked={draft.enabled}
                disabled={toggling || saving}
                label="Receber leads"
                onChange={(next) => void onToggle(next)}
              />
              {!draft.enabled ? <p className="google-ads-hint">Pausada. Novos leads não entram.</p> : null}

              <div className="google-ads-field">
                <label htmlFor="gads-column">Etapa</label>
                <select
                  id="gads-column"
                  value={draft.columnId}
                  onChange={(event) => setDraft((current) => ({ ...current, columnId: event.target.value }))}
                >
                  <option value="">Primeira do funil</option>
                  {columns.map((column) => (
                    <option key={column.id} value={column.id}>{column.name}</option>
                  ))}
                </select>
              </div>

              <div className="google-ads-field">
                <label htmlFor="gads-owner">Responsável</label>
                <select
                  id="gads-owner"
                  value={draft.assigneeId}
                  onChange={(event) => setDraft((current) => ({ ...current, assigneeId: event.target.value }))}
                >
                  <option value="">Ninguém</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </div>

              <div className="google-ads-field">
                <label htmlFor="gads-country">País do telefone</label>
                <select
                  id="gads-country"
                  value={draft.country}
                  onChange={(event) => setDraft((current) => ({ ...current, country: event.target.value }))}
                >
                  <option value="">Sem país padrão (só números com "+")</option>
                  {phoneCountryOptions().map((option) => (
                    <option key={option.code} value={option.code}>{option.label}</option>
                  ))}
                </select>
                <p className="google-ads-hint">Vale para telefones que chegam sem o "+". Número inválido para o país, ou sem país, não recebe mensagem automática. Nunca assumimos o +55.</p>
              </div>

              {draft.sourceType !== '' ? (
                <fieldset className="google-ads-fields" aria-label="Campos do formulário">
                  <legend>Campos do formulário</legend>
                  <p className="google-ads-hint">
                    Diga em qual campo chegam o nome, o telefone e o e-mail. Sem escolha, usamos os campos padrão do Google.
                  </p>
                  {([
                    ['nameColumnId', 'Nome', 'gads-map-name'],
                    ['phoneColumnId', 'Telefone', 'gads-map-phone'],
                    ['emailColumnId', 'E-mail', 'gads-map-email'],
                  ] as const).map(([key, label, id]) => (
                    <div className="google-ads-field" key={key}>
                      <label htmlFor={id}>{label}</label>
                      <select
                        id={id}
                        value={draft[key]}
                        onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))}
                      >
                        <option value="">Padrão do Google</option>
                        {seenColumnOptions(seenColumns, draft[key]).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                  {seenColumns.length === 0 ? (
                    <p className="google-ads-hint">Os campos aparecem aqui depois do primeiro envio de teste.</p>
                  ) : null}
                </fieldset>
              ) : null}

              <div className="google-ads-actions">
                {showCreate ? (
                  <button type="button" className="google-ads-btn" onClick={() => void onCreate()} disabled={saving || !draft.name.trim()}>
                    {saving ? 'Criando…' : 'Criar integração'}
                  </button>
                ) : (
                  <button type="button" className="google-ads-btn" onClick={() => void onSave()} disabled={saving || !draft.name.trim()}>
                    {saving ? 'Salvando…' : 'Salvar'}
                  </button>
                )}
                {showCreate && items.length > 0 ? (
                  <button type="button" className="google-ads-btn is-secondary" onClick={cancelCreate} disabled={saving}>
                    Cancelar
                  </button>
                ) : null}
              </div>

              {!showCreate && selected ? (
                <>
                  <div className="google-ads-secret-block">
                    <div className="google-ads-copy-row">
                      <div className="google-ads-copy-text">
                        <span className="google-ads-field-label">URL</span>
                        <span className="google-ads-url">{selected.webhookUrl}</span>
                      </div>
                      <button
                        type="button"
                        className="google-ads-btn is-secondary"
                        onClick={() => copyText(selected.webhookUrl).then(() => setNotice('URL copiado.')).catch(() => setError('Não foi possível copiar.'))}
                      >
                        Copiar URL
                      </button>
                    </div>

                    {secret ? (
                      <div className="google-ads-secret" role="status">
                        <p>Copie a chave agora. Ela não aparece de novo.</p>
                        <code>{secret}</code>
                        <button
                          type="button"
                          className="google-ads-btn"
                          onClick={() => copyText(secret).then(() => setNotice('Chave copiada.')).catch(() => setError('Não foi possível copiar.'))}
                        >
                          Copiar chave
                        </button>
                      </div>
                    ) : (
                      <p className="google-ads-hint">A chave aparece só ao criar ou ao gerar outra.</p>
                    )}

                    <div className="google-ads-actions">
                      <button type="button" className="google-ads-btn is-secondary" onClick={() => setConfirmRotate(true)}>
                        Gerar nova chave
                      </button>
                    </div>
                  </div>

                  <ProspectRuleSection
                    sectorId={selected.sectorId}
                    integrationId={selected.id}
                    integrationName={selected.name}
                    isOfficial={Boolean(isOfficial)}
                    phoneCountry={selected.defaultPhoneCountry ?? ''}
                    sourceType={selected.sourceType}
                  />

                  <div className="google-ads-events-block">
                    <h2>Leads recentes</h2>
                    {events.length === 0 ? <p className="google-ads-hint">Nenhum lead ainda.</p> : (
                      <ul className="google-ads-events">
                        {events.map((event) => {
                          const who = [event.extractedName, event.phoneOriginal, event.email].filter(Boolean).join(' · ');
                          const failed = event.status === 'failed' || event.status === 'needs_review';
                          return (
                            <li key={event.id}>
                              <div className="google-ads-event-top">
                                <time dateTime={event.receivedAt}>{formatWhen(event.receivedAt) || '—'}</time>
                                <span>{event.isTest ? 'Teste' : 'Lead'}</span>
                                <span className={`google-ads-event-status is-${failed ? 'bad' : event.status === 'processed' ? 'ok' : 'wait'}`}>
                                  {statusLabel(event.status)}
                                </span>
                              </div>
                              {who ? <p>{who}</p> : null}
                              {event.leadSourceLabel ? <p className="google-ads-hint">{event.leadSourceLabel}</p> : null}
                              {event.phoneCountryKnown === false ? (
                                <p className="google-ads-inline-error" data-testid={`no-country-${event.id}`}>Telefone sem país ou inválido: não recebe mensagem automática</p>
                              ) : null}
                              {event.errorCode ? <p className="google-ads-inline-error">{errorLabel(event.errorCode)}</p> : null}
                              {(() => {
                                const contact = contactLine(statuses[event.id]);
                                if (!contact) return null;
                                return (
                                  <p
                                    className={contact.tone === 'bad' ? 'google-ads-inline-error' : 'google-ads-hint'}
                                    data-testid={`contact-${event.id}`}
                                  >
                                    Contato: {contact.href ? <a href={contact.href}>{contact.text}</a> : contact.text}
                                  </p>
                                );
                              })()}
                              {event.deliveryCount > 1 ? <p className="google-ads-hint">{event.deliveryCount} envios</p> : null}
                              {failed ? (
                                <button type="button" className="google-ads-btn is-secondary" onClick={() => void onReprocess(event.id)}>
                                  Tentar de novo
                                </button>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  <details className="google-ads-help">
                    <summary>
                      <FiChevronRight aria-hidden="true" />
                      Como conectar o formulário
                    </summary>
                    <ol>
                      <li>Google Ads: Campanhas, Recursos, Formulário de lead, edite o formulário e cole o URL e a chave no webhook.</li>
                      <li>Site ou ferramenta (Zapier, Make, RD Station): envie um POST com o URL, a chave em <code>google_key</code> e os campos em <code>user_column_data</code>.</li>
                      <li>Envie um teste, escolha em "Campos do formulário" onde chegam nome, telefone e e-mail e salve.</li>
                    </ol>
                    <p>O formulário precisa perguntar o telefone.</p>
                  </details>

                  <div className="google-ads-danger-row">
                    <button type="button" className="google-ads-btn is-danger" onClick={() => setConfirmDelete(true)}>
                      Excluir integração
                    </button>
                  </div>
                </>
              ) : null}
            </section>
          </div>
        ) : null}
      </div>

      <ConfirmModal
        open={confirmRotate}
        onClose={() => setConfirmRotate(false)}
        onConfirm={onRotate}
        title="Gerar nova chave?"
        message="A chave atual deixa de funcionar. Cole a nova em cada formulário."
        confirmLabel="Gerar chave"
        cancelLabel="Cancelar"
        variant="warning"
        loading={rotating}
      />
      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onDelete}
        title="Excluir integração?"
        message="O Google para de enviar leads para este URL. Contatos já salvos continuam no CRM."
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        loading={deleting}
      />
    </div>
  );
};

export default GoogleAdsLeadsPage;
